import React, { useState } from "react";

const emptyBlog = {
  title: "",
  excerpt: "",
  content: "",
  image: "",
  author: "CYNOS",
  category: "",
  tags: [],
  published: true,
};

const splitCsv = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);

const BlogForm = ({ initial = emptyBlog, saving, onSubmit, submitLabel = "Save Blog" }) => {
  const [form, setForm] = useState({ ...emptyBlog, ...initial, tags: initial.tags || [] });
  const [tagsText, setTagsText] = useState((initial.tags || []).join(", "));

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleImageUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update("image", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = (event) => {
    event.preventDefault();
    onSubmit({ ...form, tags: splitCsv(tagsText) });
  };

  return (
    <form onSubmit={submit} className="grid gap-5 lg:grid-cols-[1fr_320px]" data-testid="admin-blog-form">
      <div className="space-y-4">
        <AdminInput label="Title" value={form.title} onChange={(value) => update("title", value)} required testid="blog-title" />
        <AdminTextarea label="Excerpt" value={form.excerpt} onChange={(value) => update("excerpt", value)} rows={3} testid="blog-excerpt" />
        <AdminTextarea label="Content HTML" value={form.content} onChange={(value) => update("content", value)} rows={14} required testid="blog-content" />
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          You can use simple HTML tags like p, h2, h3, strong, ul, li, and a.
        </p>
      </div>

      <aside className="space-y-4">
        <div className="border border-white/10 bg-cynos-card p-4">
          <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-cynos-red">Publish Settings</h3>
          <div className="mt-4 space-y-4">
            <AdminInput label="Author" value={form.author} onChange={(value) => update("author", value)} testid="blog-author" />
            <AdminInput label="Category" value={form.category} onChange={(value) => update("category", value)} testid="blog-category" />
            <AdminInput label="Tags (comma-separated)" value={tagsText} onChange={setTagsText} testid="blog-tags" />
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.published} onChange={(e) => update("published", e.target.checked)} className="accent-cynos-red h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-[0.2em]">Published</span>
            </label>
          </div>
        </div>

        <div className="border border-white/10 bg-cynos-card p-4">
          <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-cynos-red">Featured Image</h3>
          <AdminInput label="Image URL" value={form.image} onChange={(value) => update("image", value)} testid="blog-image" />
          <label className="mt-3 block cursor-pointer border border-white/20 px-4 py-3 text-center font-mono text-xs uppercase tracking-[0.2em] hover:border-white">
            Upload Image
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleImageUpload(e.target.files?.[0])} />
          </label>
          {form.image && <img src={form.image} alt="Blog preview" className="mt-4 aspect-[16/10] w-full object-cover border border-white/10" />}
        </div>

        <button disabled={saving} className="w-full bg-cynos-red px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-red-600 disabled:opacity-50">
          {saving ? "Saving..." : submitLabel}
        </button>
      </aside>
    </form>
  );
};

const AdminInput = ({ label, value, onChange, required, testid }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}{required ? " *" : ""}</span>
    <input required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid={testid} />
  </label>
);

const AdminTextarea = ({ label, value, onChange, rows = 5, required, testid }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}{required ? " *" : ""}</span>
    <textarea required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white font-mono text-sm" data-testid={testid} />
  </label>
);

export default BlogForm;
